import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Istanbul3DMap } from '../components/maps/Istanbul3DMap';
import { resolveCoords } from '../components/maps/ilceKoordinatlari';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { exportElementToPDF } from '../utils/html2pdfExport';
import html2canvas from 'html2canvas';
import { createTablePDF } from '../utils/pdfMakeTurkish';
import { tesisTuruList } from '../utils/tesisTuru';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISTANBUL_ILCELERI = [
  'Adalar', 'Arnavutköy', 'Ataşehir', 'Avcılar', 'Bağcılar', 'Bahçelievler', 'Bakırköy',
  'Başakşehir', 'Bayrampaşa', 'Beşiktaş', 'Beykoz', 'Beylikdüzü', 'Beyoğlu', 'Büyükçekmece',
  'Çatalca', 'Çekmeköy', 'Esenler', 'Esenyurt', 'Eyüpsultan', 'Fatih', 'Gaziosmanpaşa',
  'Güngören', 'Kadıköy', 'Kağıthane', 'Kartal', 'Küçükçekmece', 'Maltepe', 'Pendik',
  'Sancaktepe', 'Sarıyer', 'Silivri', 'Sultanbeyli', 'Sultangazi', 'Şile', 'Şişli',
  'Tuzla', 'Ümraniye', 'Üsküdar', 'Zeytinburnu'
];

const PRIORITY_COLORS = {
  low: '#10b981',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
  very_urgent: '#dc2626'
};

// Emlak tesis türü renk paleti (kararlı, accessible)
const TESIS_TURU_PALETTE = [
  '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#ef4444', '#14b8a6', '#a855f7', '#f97316',
  '#0ea5e9', '#84cc16', '#eab308', '#d946ef', '#22c55e',
];

const tesisColorFor = (idx) => TESIS_TURU_PALETTE[idx % TESIS_TURU_PALETTE.length];

// YIKILDI yapılar için kırmızı tonları
const YIKILDI_PALETTE = ['#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', '#ef4444', '#f87171'];
const yikildiColorFor = (idx) => YIKILDI_PALETTE[idx % YIKILDI_PALETTE.length];

// ============== 3D İstanbul Haritaları — durum renk paletleri ==============
const TESIS_MAP_STATUS = {
  arsa: { label: 'Arsa', hex: '#2fd08c' },
  yapi: { label: 'Yapı', hex: '#4d9fff' },
  kiralik: { label: 'Kiraya Verilen', hex: '#2fc4c4' },
  yikildi: { label: 'Yıkılan Yapı', hex: '#ff5b5b' },
};

const IHALE_MAP_STATUS = {
  devam_ediyor: { label: 'Devam Ediyor', hex: '#f5a623' },
  gecici_kabul: { label: 'Geçici Kabul', hex: '#2fc4c4' },
  tamamlandi: { label: 'Tamamlandı', hex: '#2fd08c' },
  ihale_surecinde: { label: 'İhale Sürecinde', hex: '#4d9fff' },
  proje_asamasinda: { label: 'Proje Aşamasında', hex: '#b48cf2' },
  planlanan: { label: 'Planlanan', hex: '#818cf8' },
  yikim_sureci: { label: 'Yıkım Süreci', hex: '#fb7185' },
  tasfiye_edildi: { label: 'Tasfiye Edildi', hex: '#94a3b8' },
  iptal_edildi: { label: 'İptal Edildi', hex: '#ff6b6b' },
  belirsiz: { label: 'Durum Girilmemiş', hex: '#7d93ab' },
};

const TESIS_TURU_LABELS = {
  sosyal_tesis: 'Sosyal Tesis', idari_bina: 'İdari Bina', kantin_kafe: 'Kantin/Kafe',
  stad: 'Stad', lojman: 'Lojman', spor_tesisi: 'Spor Tesisi', kamp_egitim: 'Kamp Eğitim',
  genclik_merkezi: 'Gençlik Merkezi', spor_salonu: 'Spor Salonu', yuzme_havuzu: 'Yüzme Havuzu',
  stadyum: 'Stadyum', yurt: 'Yurt', il_mudurlugu: 'İl Müdürlüğü', diger: 'Diğer',
};

// Eski kayıtlardaki serbest tesis türü değerlerini okunur etikete çevirir (ör. "kamp_egitim" → "Kamp Egitim")
const prettifyTur = (t) => String(t).replace(/_/g, ' ').toLocaleLowerCase('tr')
  .replace(/(^|\s)\S/g, (c) => c.toLocaleUpperCase('tr'));

const MULKIYET_LABELS = {  milli_emlak: 'Milli Emlak', ogm: 'OGM', belediye: 'Belediye',
  il_mudurlugu: 'İl Müdürlüğü', kamu_kurum: 'Kamu Kurum', diger: 'Diğer',
};

// ============== 3D Bar Custom Shape ==============
// Recharts Bar 'shape' prop'una atanır.  Ön yüz + üst yüz + sağ yüz = 3D parallelepiped.
const Bar3D = (props) => {
  const { x, y, width, height, fill } = props;
  if (height <= 0 || width <= 0) return null;
  const depth = Math.max(8, Math.min(18, width * 0.25)); // perspektif derinliği
  const darken = (hex, p = 0.6) => {
    const m = hex.replace('#', '').match(/.{2}/g) || ['66', '66', '66'];
    const [r, g, b] = m.map(h => Math.round(parseInt(h, 16) * p));
    return `rgb(${r}, ${g}, ${b})`;
  };
  const lighten = (hex, p = 1.25) => {
    const m = hex.replace('#', '').match(/.{2}/g) || ['66', '66', '66'];
    const [r, g, b] = m.map(h => Math.min(255, Math.round(parseInt(h, 16) * p)));
    return `rgb(${r}, ${g}, ${b})`;
  };
  const sideColor = darken(fill, 0.55);
  const topColor = lighten(fill, 1.2);
  // Üst yüz (parallelogram)
  const topPath = `M ${x},${y} L ${x + depth},${y - depth} L ${x + width + depth},${y - depth} L ${x + width},${y} Z`;
  // Sağ yüz
  const rightPath = `M ${x + width},${y} L ${x + width + depth},${y - depth} L ${x + width + depth},${y + height - depth} L ${x + width},${y + height} Z`;
  return (
    <g>
      {/* Ön yüz */}
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke={darken(fill, 0.8)} strokeWidth={0.5} rx={2} />
      {/* Sağ yan yüz */}
      <path d={rightPath} fill={sideColor} stroke={darken(fill, 0.75)} strokeWidth={0.5} />
      {/* Üst yüz */}
      <path d={topPath} fill={topColor} stroke={lighten(fill, 1.05)} strokeWidth={0.5} />
    </g>
  );
};

const GrafiklerPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ihaleler, setIhaleler] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emlakTesisStats, setEmlakTesisStats] = useState({ items: [], total_count: 0, unique_types: 0 });
  const [emlaklar, setEmlaklar] = useState([]);
  const [yapiTuruAyir, setYapiTuruAyir] = useState(() => {
    try { return localStorage.getItem('grafikler:yapiTuruAyir') === '1'; } catch { return false; }
  });
  const [refreshingEmlak, setRefreshingEmlak] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  const [kontrolYil, setKontrolYil] = useState('all');
  const [kontrolDurum, setKontrolDurum] = useState('all');
  const [ilceAlimTuru, setIlceAlimTuru] = useState('all');
  const [ilceDetayDialog, setIlceDetayDialog] = useState(false);
  const [selectedIlce, setSelectedIlce] = useState(null);
  const [selectedIlceIhaleler, setSelectedIlceIhaleler] = useState([]);

  useEffect(() => {
    if (!user?.is_admin && !user?.can_view_grafikler && !user?.is_viewer) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, ihalelerRes, tasksRes, tesisRes, emlakRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/users-simple`, { withCredentials: true }),
        axios.get(`${API}/ihaleler`, { withCredentials: true }),
        axios.get(`${API}/tasks?archived=false`, { withCredentials: true }),
        axios.get(`${API}/emlak-islemleri/stats/tesis-turu`, { withCredentials: true }).catch(() => ({ data: { items: [], total_count: 0, unique_types: 0 } })),
        axios.get(`${API}/emlak-islemleri/harita`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setIhaleler(ihalelerRes.data);
      setTasks(tasksRes.data);
      setEmlakTesisStats(tesisRes.data);
      setEmlaklar(emlakRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      console.error('Error details:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const refreshEmlakStats = async () => {
    setRefreshingEmlak(true);
    try {
      const res = await axios.get(`${API}/emlak-islemleri/stats/tesis-turu`, { withCredentials: true });
      setEmlakTesisStats(res.data);
    } catch (error) {
      console.error('Emlak stats refresh error:', error);
    } finally {
      setRefreshingEmlak(false);
    }
  };

  const displayMoney = (value) => {
    if (!value) return 0;
    let str = String(value).trim();
    if (!str || str === 'None' || str === 'null') return 0;
    
    // Remove currency symbols and spaces
    let cleaned = str.replace(/[^\d.,]/g, '');
    if (!cleaned) return 0;
    
    // Fix malformed inputs like "4.500.0000" (4+ trailing zeros after dot)
    // Pattern: number.number.0000+ should be treated as number.number (thousand separator)
    cleaned = cleaned.replace(/\.(\d+)(\.0{4,})/g, '.$1');  // "4.500.0000" → "4.500"
    
    // Handle formats
    if (cleaned.includes(',')) {
      // Turkish format with comma as decimal: 1.000.000,50
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // No comma
      const dotCount = (cleaned.match(/\./g) || []).length;
      
      if (dotCount > 1) {
        // Multiple dots = thousand separators: "1.000.000"
        cleaned = cleaned.replace(/\./g, '');
      } else if (dotCount === 1) {
        // Single dot - check if it's thousand separator or decimal
        const parts = cleaned.split('.');
        const afterDot = parts[1] || '';
        
        // If exactly 3 digits after dot, likely thousand separator: "4.500" = 4500
        if (afterDot.length === 3 && /^\d{3}$/.test(afterDot)) {
          cleaned = parts[0] + afterDot;  // Remove dot
        }
        // Otherwise keep dot as decimal point
      }
    }
    
    try {
      return parseFloat(cleaned) || 0;
    } catch {
      return 0;
    }
  };

  // Türkçe karakterleri normalize et - ilçe karşılaştırması için
  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .toString()
      .trim()
      .toUpperCase()
      .replace(/İ/g, 'I')
      .replace(/Ş/g, 'S')
      .replace(/Ğ/g, 'G')
      .replace(/Ü/g, 'U')
      .replace(/Ö/g, 'O')
      .replace(/Ç/g, 'C')
      .replace(/\s+/g, ''); // Tüm boşlukları kaldır
  };

  // ============== 3D Harita veri hazırlığı ==============
  const fmtTL = (v) => {
    const n = displayMoney(v);
    return n > 0 ? n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL' : '—';
  };

  // Harita 1: Tesis Envanteri (Emlak-Kamulaştırma verisi)
  const tesisMapItems = useMemo(() => emlaklar.map(e => {
    const coords = resolveCoords(e.enlem, e.boylam, e.ilce, e.id);
    if (!coords) return null;
    const baseStatus = e.tasinmaz_turu === 'yapi_yikildi'
      ? 'yikildi'
      : (e.kiraya_verilen_kurum ? 'kiralik' : (e.tasinmaz_turu === 'arsa' ? 'arsa' : 'yapi'));
    const turKodlari = tesisTuruList(e.tesis_turu);
    const turKey = turKodlari[0] || 'belirsiz';
    const status = (yapiTuruAyir && baseStatus === 'yapi')
      ? `yapi_${turKey}`
      : baseStatus;
    // Çoklu tür: "Yurt, Spor Salonu" (Diğer → serbest metin)
    const tesisTuruLabel = turKodlari.length
      ? turKodlari.map(k => (k === 'diger' ? (e.diger_tesis_turu || 'Diğer') : (TESIS_TURU_LABELS[k] || prettifyTur(k)))).join(', ')
      : null;
    const name = e.tesis_adi
      || (e.tasinmaz_turu === 'arsa'
        ? `Arsa · ${e.mahalle || e.ilce || ''}`.trim()
        : (tesisTuruLabel || 'Taşınmaz'));
    const rows = [
      { label: 'Tür', value: e.tasinmaz_turu === 'arsa' ? 'Arsa' : e.tasinmaz_turu === 'yapi_yikildi' ? 'Yapı (YIKILDI)' : 'Yapı' },
      ...(tesisTuruLabel ? [{ label: 'Tesis Türü', value: tesisTuruLabel }] : []),
      { label: 'Mahalle', value: e.mahalle || '—' },
      { label: 'Ada / Parsel', value: `${e.ada || '-'} / ${e.parsel || '-'}` },
      { label: 'Alan', value: e.alan_m2 ? `${e.alan_m2} m²` : '—' },
      { label: 'Mülkiyet', value: MULKIYET_LABELS[e.mulkiyet] || e.mulkiyet || '—' },
      ...(e.yapim_yili ? [{ label: 'Yapım Yılı', value: e.yapim_yili }] : []),
      ...(e.kiraya_verilen_kurum ? [{ label: 'Kiracı', value: e.kiraya_verilen_kurum }] : []),
      ...(e.yikim_tarihi ? [{ label: 'Yıkım Tarihi', value: e.yikim_tarihi }] : []),
    ];
    return {
      id: e.id, name, district: e.ilce || '—',
      group: e.tasinmaz_turu === 'arsa' ? 'Arsa (tür yok)' : (tesisTuruLabel || 'Tür Girilmemiş'),
      lat: coords.lat, lon: coords.lon, approx: coords.approx,
      status, rows, pct: null,
      note: e.yikim_notu || e.aciklama || '',
    };
  }).filter(Boolean), [emlaklar, yapiTuruAyir]);

  // Yapı statüsü tesis türlerine ayrıldığında dinamik durum/renk paleti
  const tesisStatusMap = useMemo(() => {
    if (!yapiTuruAyir) return TESIS_MAP_STATUS;
    const turler = [...new Set(
      tesisMapItems.filter(i => i.status.startsWith('yapi_')).map(i => i.status.slice(5))
    )].sort((a, b) => (TESIS_TURU_LABELS[a] || a).localeCompare(TESIS_TURU_LABELS[b] || b, 'tr'));
    const map = {
      arsa: TESIS_MAP_STATUS.arsa,
      kiralik: TESIS_MAP_STATUS.kiralik,
      yikildi: TESIS_MAP_STATUS.yikildi,
    };
    turler.forEach((t, idx) => {
      map[`yapi_${t}`] = {
        label: `Yapı · ${t === 'belirsiz' ? 'Tür Girilmemiş' : (TESIS_TURU_LABELS[t] || prettifyTur(t))}`,
        hex: tesisColorFor(idx),
      };
    });
    return map;
  }, [yapiTuruAyir, tesisMapItems]);

  // Harita 2: İhale Durumu (İhaleler verisi)
  const ihaleMapItems = useMemo(() => ihaleler.map(i => {
    const coords = resolveCoords(i.enlem, i.boylam, i.ilcesi, i.id);
    if (!coords) return null;
    const status = IHALE_MAP_STATUS[i.durumu] ? i.durumu : 'belirsiz';
    const fiziki = parseFloat(String(i.fiziki_ilerleme || '').replace('%', '').replace(',', '.'));
    const guncelBedel = i.guncel_sozlesme_bedeli_3 || i.guncel_sozlesme_bedeli_2 || i.guncel_sozlesme_bedeli_1 || i.sozlesme_bedeli;
    const rows = [
      { label: 'İhale No', value: i.ihale_no || '—' },
      { label: 'Yıl', value: String(i.yil || '—') },
      { label: 'Yüklenici Firma', value: i.ihaleyi_alan_firma || '—' },
      { label: 'Sözleşme Bedeli', value: fmtTL(guncelBedel) },
      { label: 'Ödenen Hakediş', value: fmtTL(i.toplam_odenen_hakedis) },
      ...(i.gecici_kabul_tarihi ? [{ label: 'Geçici Kabul', value: i.gecici_kabul_tarihi }] : []),
    ];
    return {
      id: i.id, name: i.isin_adi || i.ihale_no || 'İhale', district: i.ilcesi || '—',
      lat: coords.lat, lon: coords.lon, approx: coords.approx,
      status, rows,
      pct: isFinite(fiziki) ? Math.max(0, Math.min(100, Math.round(fiziki))) : null,
      pctLabel: 'Fiziki gerçekleşme',
      note: i.aciklama || '',
    };
  }).filter(Boolean), [ihaleler]);

  // Görev durumları - Tasks'tan al
  const taskStatusData = tasks.length > 0 ? [
    { name: 'Başlanmadı', value: tasks.filter(t => t.status === 'not_started').length, color: '#6b7280' },
    { name: 'Planlanan', value: tasks.filter(t => t.status === 'planned').length, color: '#06b6d4' },
    { name: 'Devam Ediyor', value: tasks.filter(t => t.status === 'in_progress').length, color: '#3b82f6' },
    { name: 'Proje İşleri', value: tasks.filter(t => t.status === 'project_work').length, color: '#8b5cf6' },
    { name: 'İhale Aşamasında', value: tasks.filter(t => t.status === 'tender_phase').length, color: '#f97316' },
    { name: 'Tamamlandı', value: tasks.filter(t => t.status === 'completed').length, color: '#10b981' }
  ].filter(item => item.value > 0) : [];

  // Öncelik dağılımı - Tasks'tan al
  const priorityChartData = tasks.length > 0 ? [
    { name: 'Düşük', value: tasks.filter(t => t.priority === 'low').length, fill: PRIORITY_COLORS.low },
    { name: 'Orta', value: tasks.filter(t => t.priority === 'medium').length, fill: PRIORITY_COLORS.medium },
    { name: 'Yüksek', value: tasks.filter(t => t.priority === 'high').length, fill: PRIORITY_COLORS.high },
    { name: 'Acil', value: tasks.filter(t => t.priority === 'urgent').length, fill: PRIORITY_COLORS.urgent },
    { name: 'Çok Acil', value: tasks.filter(t => t.priority === 'very_urgent').length, fill: PRIORITY_COLORS.very_urgent }
  ].filter(item => item.value > 0) : [];

  // Kullanıcı görev sayıları - Tasks'tan hesapla
  const userTaskData = users.map(u => {
    const taskCount = tasks.filter(t => 
      t.assigned_to === u.id || t.assigned_to_2 === u.id || t.assigned_to_3 === u.id
    ).length;
    return {
      name: u.name,
      gorevler: taskCount
    };
  }).filter(item => item.gorevler > 0).sort((a, b) => b.gorevler - a.gorevler);

  // İlçe bazında harcama grafiği
  const ilceHarcamaData = ISTANBUL_ILCELERI.map(ilce => {
    const ilceIhaleleri = ihaleler.filter(i => {
      // Yıl filtresi
      if (selectedYear !== 'all' && i.yil.toString() !== selectedYear) return false;
      // Alım Türü filtresi
      if (ilceAlimTuru !== 'all' && i.alim_turu !== ilceAlimTuru) return false;
      // Türkçe karakter duyarsız karşılaştırma
      return i.ilcesi && normalizeString(i.ilcesi) === normalizeString(ilce);
    });
    
    const toplam = ilceIhaleleri.reduce((sum, ihale) => {
      const hakedis = displayMoney(ihale.toplam_odenen_hakedis || '0');
      return sum + hakedis;
    }, 0);
    
    return { ilce, tutar: toplam };
  }).filter(item => item.tutar > 0).sort((a, b) => b.tutar - a.tutar);
  
  // Debug: İlçe eşleşmelerini kontrol et
  console.log('=== İLÇE DEBUG ===');
  console.log('Toplam ihale:', ihaleler.length);
  
  // Tüm unique ilçeleri listele
  const uniqueIlceler = [...new Set(ihaleler.map(i => i.ilcesi).filter(Boolean))];
  console.log('Veritabanındaki unique ilçeler:', uniqueIlceler);
  console.log('Normalize edilmiş:', uniqueIlceler.map(i => `${i} -> ${normalizeString(i)}`));
  
  // ISTANBUL_ILCELERI ile karşılaştır
  console.log('ISTANBUL_ILCELERI listesi:', ISTANBUL_ILCELERI.map(i => `${i} -> ${normalizeString(i)}`));
  
  console.log('İlçe Harcama Data:', JSON.stringify(ilceHarcamaData, null, 2));
  console.log('Toplam ihale sayısı:', ihaleler.length);
  console.log('Seçili yıl:', selectedYear);
  const detay = ihaleler.slice(0, 3).map(i => ({ 
    ilce: i.ilcesi, 
    hakedis_raw: i.toplam_odenen_hakedis,
    hakedis_parsed: displayMoney(i.toplam_odenen_hakedis || '0')
  }));
  console.log('İlk 3 ihale (detaylı):', JSON.stringify(detay, null, 2));

  // Kontrol Teşkilatı Görev Dağılımı - Her kişi kaç ihalede görev alıyor
  const kontrolTeskilatiData = users.map(user => {
    const ihaleCount = ihaleler.filter(ihale => {
      // Yıl filtresi
      if (kontrolYil !== 'all' && ihale.yil.toString() !== kontrolYil) return false;
      // Durum filtresi
      if (kontrolDurum !== 'all' && ihale.durumu !== kontrolDurum) return false;
      // Kontrol teşkilatında bu kullanıcı var mı?
      return Array.isArray(ihale.kontrol_teskilati) && ihale.kontrol_teskilati.includes(user.id);
    }).length;
    
    return {
      name: user.name,
      ihale_sayisi: ihaleCount
    };
  }).filter(item => item.ihale_sayisi > 0).sort((a, b) => b.ihale_sayisi - a.ihale_sayisi);

  console.log('Kontrol Teşkilatı Data:', kontrolTeskilatiData);

  // İlçe tıklama handler
  const handleIlceClick = (data) => {
    if (!data || !data.ilce) return;
    
    const ilceIhaleleri = ihaleler.filter(i => {
      // Yıl filtresi
      if (selectedYear !== 'all' && i.yil.toString() !== selectedYear) return false;
      // Alım Türü filtresi
      if (ilceAlimTuru !== 'all' && i.alim_turu !== ilceAlimTuru) return false;
      // Türkçe karakter duyarsız ilçe kontrolü
      return i.ilcesi && normalizeString(i.ilcesi) === normalizeString(data.ilce);
    });
    
    setSelectedIlce(data.ilce);
    setSelectedIlceIhaleler(ilceIhaleleri);
    setIlceDetayDialog(true);
  };

  // İlçe detay PDF export - html2canvas yöntemi (%100 Türkçe karakter garantisi)
  const exportIlceDetayPDF = async () => {
    if (!selectedIlceIhaleler.length) return;
    
    try {
      // Dialog içeriğini bul
      const dialogElement = document.querySelector('[role="dialog"]');
      if (!dialogElement) {
        alert('Dialog bulunamadı');
        return;
      }
      
      await exportElementToPDF(dialogElement, `${selectedIlce}-yatirimlar.pdf`, 'landscape');
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const exportChartAsPDF = async (chartId, chartName) => {
    try {
      const chartElement = document.getElementById(chartId);
      if (!chartElement) {
        alert('Grafik bulunamadı');
        return;
      }

      const canvas = await html2canvas(chartElement, { scale: 2, backgroundColor: '#111827' });
      const imgData = canvas.toDataURL('image/png');
      
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      doc.setFontSize(16);
      doc.text(chartName, 14, 15);
      doc.setFontSize(10);
      doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, 14, 22);
      
      const imgWidth = 180;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      doc.addImage(imgData, 'PNG', 15, 30, imgWidth, imgHeight);
      
      doc.save(`${chartName.toLowerCase().replace(/ /g, '-')}.pdf`);
    } catch (error) {
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const exportAllChartsAsPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      doc.setFontSize(18);
      doc.text('Grafikler Raporu', 14, 15);
      doc.setFontSize(10);
      doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, 14, 22);

      // Her grafik için screenshot al ve PDF'e ekle
      const charts = ['task-status-chart', 'priority-chart', 'user-task-chart', 'kontrol-teskilati-chart', 'ilce-harcama-chart'];
      let yPos = 30;

      for (const chartId of charts) {
        const chartElement = document.getElementById(chartId);
        if (chartElement) {
          const canvas = await html2canvas(chartElement, { scale: 2 });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 180;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          if (yPos + imgHeight > 280) {
            doc.addPage();
            yPos = 15;
          }
          
          doc.addImage(imgData, 'PNG', 15, yPos, imgWidth, imgHeight);
          yPos += imgHeight + 10;
        }
      }

      doc.save('gsb-grafikler.pdf');
    } catch (error) {
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="GSB Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Grafikler</h1>
                <p className="text-xs text-gray-400">İstatistikler ve Raporlar</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => navigate('/calendar')} variant="ghost" className="text-gray-300 hover:text-white">Takvim</Button>
              {(user?.is_admin || user?.can_access_finance_module) && (
                <>
                  <Button onClick={() => navigate('/finance/ihaleler')} variant="ghost" className="text-gray-300 hover:text-white">İhaleler</Button>
                  <Button onClick={() => navigate('/finance/odenek')} variant="ghost" className="text-gray-300 hover:text-white">Ödenek</Button>
                </>
              )}
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">📊 Grafikler ve Analizler</h2>
            <p className="text-gray-400">İstatistikler, grafikler ve raporlar</p>
          </div>
          <Button onClick={() => navigate('/grafikler/sunum-olusturucu')} className="bg-red-600 hover:bg-red-700 text-white mr-2" data-testid="grafikler-sunum-btn">
            🎬 {user?.is_viewer ? 'Sunum Görüntüle' : 'Sunum Oluşturucu'}
          </Button>
          <Button onClick={exportAllChartsAsPDF} className="bg-blue-600 hover:bg-blue-700 text-white">
            📄 Tüm Grafikleri PDF İndir
          </Button>
        </div>

        {/* ============ 3D İstanbul Haritası — Tesis Envanteri ============ */}
        <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 border-gray-800 mb-6" data-testid="tesis-envanter-3d-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-2xl">🗺️</span> İstanbul 3D Tesis Envanteri Haritası
                </CardTitle>
                <p className="text-xs text-gray-400 mt-1">
                  Emlak-Kamulaştırma envanteri · {tesisMapItems.length} taşınmaz haritada
                  {emlaklar.length - tesisMapItems.length > 0 && (
                    <span className="text-amber-400/80"> · {emlaklar.length - tesisMapItems.length} kayıt ilçesiz (gösterilemiyor)</span>
                  )}
                  {tesisMapItems.filter(i => i.approx).length > 0 && (
                    <span className="text-sky-400/80"> · {tesisMapItems.filter(i => i.approx).length} kayıt yaklaşık konumda (ilçe merkezi — kesikli pin)</span>
                  )}
                  <span className="text-gray-500"> · Gerçek OSM 3D binaları için haritaya yakınlaşın</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setYapiTuruAyir(v => {
                    try { localStorage.setItem('grafikler:yapiTuruAyir', v ? '0' : '1'); } catch { /* kota */ }
                    return !v;
                  })}
                  size="sm"
                  className={yapiTuruAyir
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'}
                  data-testid="tesis-turu-ayir-toggle"
                >
                  {yapiTuruAyir ? '🎨 Tesis Türüne Göre: Açık' : '🎨 Tesis Türüne Göre Ayır'}
                </Button>
                {(user?.is_admin || user?.has_emlak_kamulastirma_access) && (
                  <Button
                    onClick={() => navigate('/emlak-kamulastirma/emlak-islemleri')}
                    size="sm"
                    variant="outline"
                    className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
                  >
                    Emlak İşlemleri →
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {tesisMapItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">Haritada gösterilecek taşınmaz kaydı bulunamadı.</p>
                <p className="text-xs text-gray-500 mt-2">Emlak modülünde ilçe veya koordinat bilgisi girilen kayıtlar burada görüntülenir.</p>
              </div>
            ) : (
              <Istanbul3DMap
                items={tesisMapItems}
                statusMap={tesisStatusMap}
                onOpenRecord={() => navigate('/emlak-kamulastirma/emlak-islemleri')}
                openRecordLabel="Emlak kaydını aç →"
                height={620}
                testIdPrefix="tesis3d"
                exportTitle="İstanbul Tesis Envanteri (Harita Filtresi)"
                exportFileName="tesis-envanteri"
              />
            )}
          </CardContent>
        </Card>

        {/* ============ 3D İstanbul Haritası — İhale Durumu ============ */}
        <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 border-gray-800 mb-6" data-testid="ihale-durum-3d-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-2xl">🏗️</span> İstanbul 3D İhale Durumu Haritası
                </CardTitle>
                <p className="text-xs text-gray-400 mt-1">
                  İhaleler modülü · {ihaleMapItems.length} iş haritada · Durum ve ilçe filtreleriyle süzün
                  {ihaleler.length - ihaleMapItems.length > 0 && (
                    <span className="text-amber-400/80"> · {ihaleler.length - ihaleMapItems.length} kayıt ilçesiz (gösterilemiyor)</span>
                  )}
                  {ihaleMapItems.filter(i => i.approx).length > 0 && (
                    <span className="text-sky-400/80"> · {ihaleMapItems.filter(i => i.approx).length} kayıt yaklaşık konumda (ilçe merkezi — kesikli pin)</span>
                  )}
                </p>
              </div>
              {(user?.is_admin || user?.can_access_finance_module) && (
                <Button
                  onClick={() => navigate('/finance/ihaleler')}
                  size="sm"
                  variant="outline"
                  className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
                >
                  İhaleler →
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {ihaleMapItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">Haritada gösterilecek ihale kaydı bulunamadı.</p>
                <p className="text-xs text-gray-500 mt-2">İhale kayıtlarında ilçe veya koordinat bilgisi girildiğinde burada görüntülenir.</p>
              </div>
            ) : (
              <Istanbul3DMap
                items={ihaleMapItems}
                statusMap={IHALE_MAP_STATUS}
                onOpenRecord={() => navigate('/finance/ihaleler')}
                openRecordLabel="İhale kaydını aç →"
                height={620}
                testIdPrefix="ihale3d"
                exportTitle="İhale Durumu (Harita Filtresi)"
                exportFileName="ihale-durumu"
              />
            )}
          </CardContent>
        </Card>

        {/* Emlak Tesis Türü Envanteri (3D Sütun Grafik) */}
        <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 border-gray-800 mb-6" id="emlak-tesis-turu-chart" data-testid="emlak-tesis-turu-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-2xl">🏗️</span> Emlak Tesis Türü Envanteri
                </CardTitle>
                <p className="text-xs text-gray-400 mt-1">
                  Toplam {emlakTesisStats.total_count} yapı · {emlakTesisStats.unique_types} farklı tesis türü
                  {typeof emlakTesisStats.total_active === 'number' && (
                    <span className="ml-2 text-emerald-400">· {emlakTesisStats.total_active} aktif</span>
                  )}
                  {typeof emlakTesisStats.total_yikildi === 'number' && emlakTesisStats.total_yikildi > 0 && (
                    <span className="ml-2 text-red-400">· {emlakTesisStats.total_yikildi} yıkılmış</span>
                  )}
                  {emlakTesisStats.generated_at && (
                    <span className="ml-2 text-gray-500">
                      · Güncelleme: {new Date(emlakTesisStats.generated_at).toLocaleTimeString('tr-TR')}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={refreshEmlakStats}
                  disabled={refreshingEmlak}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="emlak-tesis-refresh-btn"
                >
                  {refreshingEmlak ? '⟳' : '🔄'} Yenile
                </Button>
                <Button onClick={() => exportChartAsPDF('emlak-tesis-turu-chart', 'Emlak Tesis Türü Envanteri')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  📄 PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {emlakTesisStats.items.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">Henüz yapı türünde emlak kaydı bulunmuyor.</p>
                <p className="text-xs text-gray-500 mt-2">Emlak modülünde "Tesis Türü" alanı dolu olan yapı kayıtları burada gösterilecek.</p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart
                    data={emlakTesisStats.items}
                    margin={{ top: 30, right: 40, left: 10, bottom: 60 }}
                  >
                    <defs>
                      {emlakTesisStats.items.map((item, idx) => {
                        const baseColor = item.yikildi ? yikildiColorFor(idx) : tesisColorFor(idx);
                        return (
                          <linearGradient key={`grad-${idx}`} id={`tesis-grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={baseColor} stopOpacity={1} />
                            <stop offset="100%" stopColor={baseColor} stopOpacity={0.75} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                    <XAxis
                      dataKey="label"
                      stroke="#9ca3af"
                      tick={{ fill: '#d1d5db', fontSize: 12 }}
                      angle={-25}
                      textAnchor="end"
                      height={70}
                      interval={0}
                    />
                    <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }}
                      labelStyle={{ color: '#fff', fontWeight: 600 }}
                      formatter={(value, name, props) => {
                        if (name === 'count') {
                          const alan = props.payload?.total_alan_m2 || 0;
                          return [`${value} adet (${alan.toLocaleString('tr-TR')} m²)`, 'Yapı Sayısı'];
                        }
                        return [value, name];
                      }}
                    />
                    <Bar
                      dataKey="count"
                      shape={<Bar3D />}
                      isAnimationActive={true}
                      animationDuration={800}
                      label={{ position: 'top', fill: '#fff', fontSize: 13, fontWeight: 700 }}
                    >
                      {emlakTesisStats.items.map((entry, idx) => (
                        <Cell key={`tcell-${idx}`} fill={`url(#tesis-grad-${idx})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Liste özeti */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {emlakTesisStats.items.map((item, idx) => (
                    <div
                      key={`${item.tesis_turu}-${item.yikildi ? 'yik' : 'akt'}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        item.yikildi
                          ? 'bg-red-950/30 border-red-800/40 hover:border-red-600/60'
                          : 'bg-gray-800/40 border-gray-700/40 hover:border-gray-600/60'
                      }`}
                      data-testid={`tesis-summary-${item.tesis_turu}${item.yikildi ? '-yikildi' : ''}`}
                    >
                      <div
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ background: item.yikildi ? yikildiColorFor(idx) : tesisColorFor(idx) }}
                      />
                      <span className={`text-xs truncate flex-1 ${item.yikildi ? 'text-red-200' : 'text-gray-300'}`} title={item.label}>
                        {item.label}
                      </span>
                      <span className="text-sm font-bold text-white">{item.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Görev Durum Grafiği */}
        <Card className="bg-gray-900 border-gray-800 mb-6" id="task-status-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Görev Durumları</CardTitle>
              <Button onClick={() => exportChartAsPDF('task-status-chart', 'Görev Durumları')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                📄 PDF İndir
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={taskStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {taskStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Öncelik Dağılımı */}
        <Card className="bg-gray-900 border-gray-800 mb-6" id="priority-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Öncelik Dağılımı</CardTitle>
              <Button onClick={() => exportChartAsPDF('priority-chart', 'Öncelik Dağılımı')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                📄 PDF İndir
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={priorityChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {priorityChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Çalışan Görev Sayıları */}
        <Card className="bg-gray-900 border-gray-800 mb-6" id="user-task-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Çalışan Başına Görev Sayıları</CardTitle>
              <Button onClick={() => exportChartAsPDF('user-task-chart', 'Çalışan Görev Sayıları')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                📄 PDF İndir
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={userTaskData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={100} />
                <YAxis stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                <Legend />
                <Bar dataKey="gorevler" fill="#3b82f6" name="Toplam Görev" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Kontrol Teşkilatı İhale Dağılımı */}
        <Card className="bg-gray-900 border-gray-800 mb-6" id="kontrol-teskilati-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Kontrol Teşkilatı İhale Dağılımı</CardTitle>
                <p className="text-xs text-gray-400 mt-1">Her personelin kontrol ettiği ihale sayısı</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={kontrolYil} onValueChange={setKontrolYil}>
                  <SelectTrigger className="w-32 bg-gray-800 border-gray-700 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Tüm Yıllar</SelectItem>
                    {[...new Set(ihaleler.map(i => i.yil))].sort((a, b) => b - a).map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={kontrolDurum} onValueChange={setKontrolDurum}>
                  <SelectTrigger className="w-44 bg-gray-800 border-gray-700 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="iptal_edildi">İptal Edildi</SelectItem>
                    <SelectItem value="yikim_sureci">Yıkım Süreci</SelectItem>
                    <SelectItem value="tasfiye_edildi">Tasfiye Edildi</SelectItem>
                    <SelectItem value="proje_asamasinda">Proje Aşamasında</SelectItem>
                    <SelectItem value="planlanan">Planlanan</SelectItem>
                    <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                    <SelectItem value="devam_ediyor">Devam Ediyor</SelectItem>
                    <SelectItem value="ihale_surecinde">İhale Sürecinde</SelectItem>
                    <SelectItem value="gecici_kabul">Geçici Kabul Aşamasında</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => exportChartAsPDF('kontrol-teskilati-chart', 'Kontrol Teşkilatı Dağılımı')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  📄 PDF İndir
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {kontrolTeskilatiData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={kontrolTeskilatiData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', color: '#fff' }}
                    formatter={(value) => [value + ' İhale', 'Görev Sayısı']}
                  />
                  <Legend wrapperStyle={{ color: '#fff' }} />
                  <Bar 
                    dataKey="ihale_sayisi" 
                    name="İhale Sayısı" 
                    fill="#8b5cf6"
                    radius={[8, 8, 0, 0]}
                    label={{ 
                      position: 'top', 
                      fill: '#fff',
                      fontSize: 12
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-400">
                Kontrol Teşkilatı verisi yok
              </div>
            )}
          </CardContent>
        </Card>

        {/* İstanbul İlçeleri Harcama Grafiği */}
        <Card className="bg-gray-900 border-gray-800" id="ilce-harcama-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">İstanbul İlçelerine Yapılan Yatırımlar</CardTitle>
              <div className="flex items-center gap-3">
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-40 bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Tüm Yıllar</SelectItem>
                    {[...new Set(ihaleler.map(i => i.yil))].sort((a, b) => b - a).map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={ilceAlimTuru} onValueChange={setIlceAlimTuru}>
                  <SelectTrigger className="w-44 bg-gray-800 border-gray-700 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Tüm Alım Türleri</SelectItem>
                    <SelectItem value="yapim">Yapım</SelectItem>
                    <SelectItem value="proje_isleri">Proje İşleri</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => exportChartAsPDF('ilce-harcama-chart', 'İlçe Yatırımları')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  📄 PDF İndir
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={ilceHarcamaData} onClick={(data) => {
                if (data && data.activePayload && data.activePayload[0]) {
                  handleIlceClick(data.activePayload[0].payload);
                }
              }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="ilce" stroke="#9ca3af" angle={-45} textAnchor="end" height={120} />
                <YAxis 
                  stroke="#9ca3af" 
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toFixed(0);
                  }} 
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', color: '#fff' }}
                  formatter={(value) => [value.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL', 'Yatırım']}
                />
                <Legend wrapperStyle={{ color: '#fff' }} />
                <Bar 
                  dataKey="tutar" 
                  name="Yapılan Yatırımlar (TL)" 
                  radius={[8, 8, 0, 0]}
                  onClick={handleIlceClick}
                  cursor="pointer"
                  label={{ 
                    position: 'top', 
                    fill: '#fff',
                    fontSize: 11,
                    formatter: (value) => {
                      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                      return value.toFixed(0);
                    }
                  }}
                >
                  {ilceHarcamaData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`hsl(${(index * 360) / Math.max(ilceHarcamaData.length, 1)}, 70%, 55%)`}
                      onClick={() => handleIlceClick(entry)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            
            {/* İlçelere hızlı erişim butonları */}
            <div className="mt-6 flex flex-wrap gap-2">
              {ilceHarcamaData.map((item) => (
                <Button
                  key={item.ilce}
                  onClick={() => handleIlceClick(item)}
                  size="sm"
                  variant="outline"
                  className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
                >
                  {item.ilce} ({(item.tutar / 1000000).toFixed(2)}M TL)
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* İlçe Detay Dialog */}
        <Dialog open={ilceDetayDialog} onOpenChange={setIlceDetayDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto bg-gray-900 border-gray-800">
            <DialogHeader>
              <DialogTitle className="text-white text-2xl">
                {selectedIlce} - Yatırım Detayları
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Bu ilçeye yapılan tüm yatırımlar ve ihaleler
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-white">
                  <p className="text-sm text-gray-400">Toplam İhale Sayısı</p>
                  <p className="text-2xl font-bold">{selectedIlceIhaleler.length}</p>
                </div>
                <div className="text-white">
                  <p className="text-sm text-gray-400">Toplam Yatırım</p>
                  <p className="text-2xl font-bold text-green-400">
                    {selectedIlceIhaleler.reduce((sum, ihale) => {
                      return sum + displayMoney(ihale.toplam_odenen_hakedis || '0');
                    }, 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL
                  </p>
                </div>
                <Button onClick={exportIlceDetayPDF} className="bg-blue-600 hover:bg-blue-700">
                  📄 PDF İndir
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300">
                  <thead className="text-xs uppercase bg-gray-800 text-gray-400">
                    <tr>
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Yıl</th>
                      <th className="px-4 py-3">İhale No</th>
                      <th className="px-4 py-3">İşin Adı</th>
                      <th className="px-4 py-3">Alım Türü</th>
                      <th className="px-4 py-3">Durum</th>
                      <th className="px-4 py-3 text-right">Yaklaşık Maliyet</th>
                      <th className="px-4 py-3 text-right">Sözleşme Bedeli</th>
                      <th className="px-4 py-3 text-right">Ödenen Hakediş</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedIlceIhaleler.length > 0 ? (
                      selectedIlceIhaleler.map((ihale, idx) => (
                        <tr key={ihale.id} className="border-b border-gray-700 hover:bg-gray-800">
                          <td className="px-4 py-3">{idx + 1}</td>
                          <td className="px-4 py-3">{ihale.yil}</td>
                          <td className="px-4 py-3 font-medium">{ihale.ihale_no}</td>
                          <td className="px-4 py-3">{ihale.isin_adi || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">
                              {ihale.alim_turu || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={ihale.durumu === 'tamamlandi' ? 'default' : 'secondary'} className="text-xs">
                              {ihale.durumu || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {displayMoney(ihale.yaklasik_maliyet || '0').toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL
                          </td>
                          <td className="px-4 py-3 text-right">
                            {displayMoney(ihale.sozlesme_bedeli || '0').toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-400">
                            {displayMoney(ihale.toplam_odenen_hakedis || '0').toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-gray-400">
                          Bu ilçe için henüz yatırım kaydı bulunmuyor
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => setIlceDetayDialog(false)} variant="outline" className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
                Kapat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default GrafiklerPage;
